import { Disposable } from '@gitlab-org/disposable';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { Notifier, NotifyFn } from '../notifier';
import { FeatureStateNotificationParams } from '../notifications';
import { getEntries } from '../utils/get_entries';
import { log } from '../log';
import { StateCheck } from './state_check';
import { CodeSuggestionsSupportedLanguageCheck } from './supported_language_check';
import { ProjectDuoAccessCheck } from './project_duo_acces_check';
import {
  FeatureState,
  CHECKS_PER_FEATURE,
  StateCheckId,
  FeatureStateCheck,
} from './feature_state_management_types';
import { CodeSuggestionsDuoLicenseCheck } from './code_suggestions_duo_license_check';
import { CodeSuggestionsInstanceVersionCheck } from './minimal_gitlab_version_for_code_suggestions_check';

const sortEngagedChecksInPriority = (
  orderedChecks: StateCheckId[],
  engagedChecks: StateCheck<StateCheckId>[],
) => {
  const indexMap = new Map<StateCheckId, number>();
  orderedChecks.forEach((checkId: StateCheckId, index: number) => indexMap.set(checkId, index));

  const unsortedEngagedChecked = [...engagedChecks];

  return unsortedEngagedChecked.sort((a, b) => {
    const indexA = indexMap.get(a.id);
    const indexB = indexMap.get(b.id);

    // If both indices are found, sort by index
    if (indexA !== undefined && indexB !== undefined) {
      return indexA - indexB;
    }

    // If one index is found and the other is not, prioritize the found one
    if (indexA !== undefined) return -1;
    if (indexB !== undefined) return 1;

    // If neither index is found, maintain original order
    return 0;
  });
};

export interface FeatureStateManager extends Notifier<FeatureStateNotificationParams>, Disposable {}

export const FeatureStateManager = createInterfaceId<FeatureStateManager>('FeatureStateManager');

@Injectable(FeatureStateManager, [
  CodeSuggestionsInstanceVersionCheck,
  CodeSuggestionsDuoLicenseCheck,
  ProjectDuoAccessCheck,
  CodeSuggestionsSupportedLanguageCheck,
])
export class DefaultFeatureStateManager implements FeatureStateManager {
  #checks: StateCheck<StateCheckId>[] = [];

  #subscriptions: Disposable[] = [];

  #notify?: NotifyFn<FeatureStateNotificationParams>;

  constructor(
    codeSuggestionsInstanceVersionCheck: CodeSuggestionsInstanceVersionCheck,
    codeSuggestionsDuoLicenseCheck: CodeSuggestionsDuoLicenseCheck,
    projectDuoAccessCheck: ProjectDuoAccessCheck,
    supportedLanguagePolicy: CodeSuggestionsSupportedLanguageCheck,
  ) {
    this.#checks.push(
      codeSuggestionsInstanceVersionCheck,
      codeSuggestionsDuoLicenseCheck,
      projectDuoAccessCheck,
      supportedLanguagePolicy,
    );
  }

  async init(notify: NotifyFn<FeatureStateNotificationParams>): Promise<void> {
    this.#notify = notify;

    this.#subscriptions.push(
      ...this.#checks.map((check) => check.onChanged(() => this.#notifyClient())),
    );

    try {
      await Promise.all(this.#checks.filter((c) => Boolean(c.init)).map((c) => c.init?.()));
    } catch (e) {
      log.error('Failed to initialize some feature state checks', e);
    }
  }

  async #notifyClient() {
    if (!this.#notify) {
      throw new Error(
        "The state manager hasn't been initialized. It can't send notifications. Call the init method first.",
      );
    }
    await this.#notify(this.#state);
  }

  dispose() {
    this.#subscriptions.forEach((s) => s.dispose());
  }

  get #state(): FeatureState[] {
    const engagedChecks = this.#checks.filter((check) => check.engaged);

    return getEntries(CHECKS_PER_FEATURE).map(([featureId, stateChecks]) => {
      const engagedFeatureChecks: FeatureStateCheck<StateCheckId>[] = sortEngagedChecksInPriority(
        stateChecks,
        engagedChecks,
      )
        .filter(({ id }) => stateChecks.includes(id))
        .map((stateCheck) => ({
          checkId: stateCheck.id,
          details: stateCheck.details,
          context: stateCheck.context,
        }));

      return {
        featureId,
        engagedChecks: engagedFeatureChecks,
      };
    });
  }
}
